import { eventEntityId, type DomainEvent, type DomainEventType, type EventOf } from '../events';
import type { ModuleId } from '../modules';
import type { Mutation } from '../mutations';
import type { ReadModel } from './readModel';

export interface RuleContext {
  userId: string;
  tz: string;
  now: string;
  usable(moduleId: ModuleId): boolean;
  read: ReadModel;
}

/**
 * What a rule returns: mutations only, or mutations plus follow-up events. `connected` says the
 * rule took part even though it wrote nothing itself (its effect was folded into the producer's
 * own write), so its modules still join the chain for the connection toast.
 */
export type RuleResult =
  Mutation[] | { mutations: Mutation[]; events?: DomainEvent[]; connected?: boolean };

export interface Rule<E extends DomainEvent = DomainEvent> {
  /** e.g. 'quit.cigarette-expense' */
  id: string;
  on: E['type'];
  /** The rule runs only if every module here is usable. */
  touches: ModuleId[];
  run(event: E, ctx: RuleContext): RuleResult;
}

/** Types a rule against its event member, then widens it so it fits in a Rule[] list. */
export function defineRule<T extends DomainEventType>(rule: Rule<EventOf<T>> & { on: T }): Rule {
  return rule as unknown as Rule;
}

/** The dispatched event is depth 0; follow-up events deeper than this are dropped. */
export const MAX_RULE_DEPTH = 3;

export interface RunRulesResult {
  mutations: Mutation[];
  /** Modules the event rippled through, in first-touched order, no duplicates (connection toast). */
  chain: ModuleId[];
}

/**
 * Runs the rules for an event, then for the follow-up events those rules emit, breadth-first,
 * with the same context.
 * - A rule runs only when every module in `touches` is usable. A skipped rule emits nothing,
 *   so an unusable module stops that branch.
 * - Cycle guard: a rule runs at most once per entity (rule id + eventEntityId).
 * - Depth: the dispatched event is depth 0. Events that would be deeper than MAX_RULE_DEPTH
 *   are dropped.
 * - causedBy: each mutation without one gets the mutationId of the event that triggered its
 *   rule. A follow-up event without a mutationId inherits its parent event's.
 * - chain: the touched modules of every rule that returned a mutation, an event, or connected.
 */
export function runRules(event: DomainEvent, ctx: RuleContext, rules: Rule[]): RunRulesResult {
  const mutations: Mutation[] = [];
  const chain: ModuleId[] = [];
  const ran = new Set<string>();
  const queue: { event: DomainEvent; depth: number }[] = [{ event, depth: 0 }];

  let item: { event: DomainEvent; depth: number } | undefined;
  while ((item = queue.shift()) !== undefined) {
    const { event: current, depth } = item;
    for (const rule of rules) {
      if (rule.on !== current.type) continue;
      if (!rule.touches.every((moduleId) => ctx.usable(moduleId))) continue;
      const key = `${rule.id}|${eventEntityId(current)}`;
      if (ran.has(key)) continue;
      ran.add(key);

      const result = rule.run(current, ctx);
      const produced = Array.isArray(result) ? result : result.mutations;
      const followUps = Array.isArray(result) ? [] : (result.events ?? []);
      const connected = !Array.isArray(result) && result.connected === true;
      if (produced.length === 0 && followUps.length === 0 && !connected) continue;

      for (const m of produced) {
        if (m.causedBy === undefined && current.mutationId !== undefined) {
          mutations.push({ ...m, causedBy: current.mutationId });
        } else {
          mutations.push(m);
        }
      }
      if (depth < MAX_RULE_DEPTH) {
        for (const next of followUps) {
          const withCause =
            next.mutationId === undefined && current.mutationId !== undefined
              ? { ...next, mutationId: current.mutationId }
              : next;
          queue.push({ event: withCause, depth: depth + 1 });
        }
      }
      for (const moduleId of rule.touches) {
        if (!chain.includes(moduleId)) chain.push(moduleId);
      }
    }
  }
  return { mutations, chain };
}
